import { NormalizedNumber } from '@sparkdotfi/common-universal'

import { trackAction } from '@/domain/analytics/cookie3'
import { useSandbox } from '@/domain/sandbox/useSandbox'
import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { resolveActivePointsSeason, selectSeasonValue } from '@/features/points/helpers'
import { useSparkPointsData } from '@/features/points/logic/useSparkPointsData'
import { useClipboard } from '@/utils/useClipboard'

export interface UseReferralShareDialogResult {
  referralCode: string
  season2TotalPoints: NormalizedNumber
  season3TotalPoints: NormalizedNumber
  pointsSeason: number
  invitedUsers: number
  shareUrl: string
  onCopy: () => Promise<void>
  onShare: () => void
}

export function useReferralShareDialog(): UseReferralShareDialogResult {
  const { account } = useConnectedAddress()
  const { pointsData } = useSparkPointsData({ account })
  const { copy } = useClipboard()
  const referralUrl = buildReferralUrl(pointsData.referralCode)
  const { isInSandbox } = useSandbox()
  const pointsSeason = resolveActivePointsSeason()

  async function onCopy(): Promise<void> {
    await copy(referralUrl)
    trackAction({
      name: 'Points_Copy_Link',
      isInSandbox,
    })
  }

  function onShare(): void {
    trackAction({
      name: 'Points_Share',
      isInSandbox,
    })
  }

  return {
    referralCode: pointsData.referralCode,
    invitedUsers: pointsData.invitedUsers,
    season2TotalPoints: pointsData.season2TotalPoints,
    season3TotalPoints: pointsData.season3TotalPoints,
    pointsSeason,
    shareUrl: getTwitterShareUrl({ referralUrl, pointsSeason }),
    onCopy,
    onShare,
  }
}

const twitterIntentUrl = 'https://x.com/intent/tweet'

function getTwitterShareUrl({ referralUrl, pointsSeason }: { referralUrl: string; pointsSeason: number }): string {
  const text = selectSeasonValue(pointsSeason, {
    2: `Spark Points Season 2 kicking off! Grab your referral code now with @sparkdotfi: ${referralUrl}`,
    3: `Spark Points Season 3 kicking off! Grab your referral code now with @sparkdotfi: ${referralUrl}`,
  })

  return `${twitterIntentUrl}?${new URLSearchParams({ text })}`
}

export function buildReferralUrl(referralCode: string): string {
  const baseUrl = import.meta.env.STORYBOOK_PREVIEW === '1' ? 'https://app.spark.fi' : window.location.origin
  return `${baseUrl}/points/${referralCode}`
}
